import {
  Button as SystemButton,
  ButtonProps as SystemButtonProps,
} from '@hover/blueprint';
import { captureException } from '@sentry/react';
import React, { MouseEventHandler, useEffect, useState } from 'react';
import invariant from 'tiny-invariant';

import { useCurrentPage } from 'src/shared/hooks/useCurrentPage';
import { ButtonPressed, buttonPressed } from 'src/shared/telemetry';

type TrackHandler = (
  properties: ButtonPressed & { button_or_link: string },
) => void;

export type ButtonProps = SystemButtonProps & {
  pageName?: string;
  primaryCallToAction?: boolean;
  buttonLocation?: string;
  track?: TrackHandler;
};

const Button = ({
  pageName,
  primaryCallToAction = false,
  buttonLocation,
  children,
  disabled,
  label,
  track,
  type,
  ...props
}: ButtonProps) => {
  const currentPage = useCurrentPage();
  const page = pageName || currentPage;
  const buttonText = typeof children === 'string' ? children : label;

  // Disable form submission until client-side hydration
  const [isSubmitDisabled, setIsSubmitDisabled] = useState(true);

  useEffect(() => setIsSubmitDisabled(false), []);

  const onButtonPress: MouseEventHandler<HTMLButtonElement> = event => {
    props.onPress?.(event);

    try {
      invariant(page, 'Button: pageName is required when current page is null');
      invariant(
        buttonText,
        'Button: label is required when children is not string',
      );

      const properties = {
        page_or_screen_name: page,
        primary_cta: primaryCallToAction,
        button_location: buttonLocation,
        button_text: buttonText,
      };

      if (track) {
        track({ button_or_link: 'button', ...properties });
      } else {
        buttonPressed(properties);
      }
    } catch (error) {
      captureException(error);
    }
  };

  return (
    <SystemButton
      {...props}
      disabled={disabled}
      label={label}
      onPress={onButtonPress}
      type={isSubmitDisabled && type === 'submit' ? 'button' : type}
    >
      {children}
    </SystemButton>
  );
};

export { Button };
